import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { lireTexte, arreterLecture } from './klarysApi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Check, Copy, CornerDownLeft, FileText, Loader2, Mic, Pencil, Share2, Sparkles, Star, ThumbsDown, ThumbsUp, User, Volume2, VolumeX, X } from 'lucide-react';

const BoutonLire = ({ text, headers, testId }) => {
  const [etat, setEtat] = useState('repos');
  const clic = async () => {
    if (etat === 'lecture') { arreterLecture(); setEtat('repos'); return; }
    setEtat('chargement');
    try {
      const p = lireTexte(text, headers);
      setEtat('lecture');
      await p;
    } catch (e) { toast.error(e.message); } finally { setEtat('repos'); }
  };
  return (
    <button type="button" onClick={clic} data-testid={`${testId}-lire`} aria-label="Lire à voix haute"
      className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#0a3d62]">
      {etat === 'chargement' ? <Loader2 className="h-3 w-3 animate-spin" /> : etat === 'lecture' ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
      {etat === 'lecture' ? 'Arrêter' : 'Écouter'}
    </button>
  );
};

const copier = async (text, setCopie) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const t = document.createElement('textarea');
    t.value = text; document.body.appendChild(t); t.select(); document.execCommand('copy'); t.remove();
  }
  setCopie(true); toast.success('Demande copiée'); setTimeout(() => setCopie(false), 1500);
};

const copierRiche = async (el, md, setCopie) => {
  const html = `<div style="font-family:Calibri,Arial,sans-serif;font-size:11pt">${(el?.innerHTML || '')
    .replace(/<table/g, '<table border="1" cellpadding="4" style="border-collapse:collapse;border:1px solid #94a3b8"')
    .replace(/<th(?=[\s>])/g, '<th style="background:#f1f5f9;text-align:left"')}</div>`;
  try {
    if (window.ClipboardItem && navigator.clipboard?.write) {
      await navigator.clipboard.write([new window.ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }), 'text/plain': new Blob([md], { type: 'text/plain' }) })]);
    } else await navigator.clipboard.writeText(md);
  } catch {
    try { await navigator.clipboard.writeText(md); } catch { toast.error('Copie impossible'); return; }
  }
  setCopie(true); toast.success('Réponse copiée avec sa mise en forme'); setTimeout(() => setCopie(false), 1500);
};

const EditionBulle = ({ text, testId, busy, onAnnuler, onRenvoyer }) => {
  const [v, setV] = useState(text);
  const ok = v.trim() && !busy;
  return (
    <div className="space-y-1.5">
      <textarea autoFocus value={v} onChange={(e) => setV(e.target.value)} rows={Math.min(8, Math.max(2, v.split('\n').length))} data-testid={`${testId}-edition`}
        onKeyDown={(e) => { if (e.key === 'Escape') onAnnuler(); if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (ok) onRenvoyer(v.trim()); } }}
        className="w-full min-w-[260px] resize-y rounded-md border border-sky-300 bg-white px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-300" />
      <div className="flex items-center justify-end gap-2 text-[11px]">
        <span className="mr-auto text-sky-200">Envoyée comme nouvelle demande</span>
        <button type="button" onClick={onAnnuler} className="inline-flex items-center gap-0.5 rounded px-2 py-0.5 text-sky-100 hover:bg-white/10" data-testid={`${testId}-annuler`}><X className="h-3 w-3" /> Annuler</button>
        <button type="button" disabled={!ok} onClick={() => onRenvoyer(v.trim())} className="inline-flex items-center gap-0.5 rounded bg-amber-300 px-2 py-0.5 font-semibold text-[#0a3d62] disabled:opacity-50" data-testid={`${testId}-renvoyer`}><CornerDownLeft className="h-3 w-3" /> Renvoyer</button>
      </div>
    </div>
  );
};

const Avis = ({ testId, onAvis }) => {
  const [note, setNote] = useState(null);
  const [ouvert, setOuvert] = useState(false);
  const [com, setCom] = useState('');
  const envoyer = async (n, c = '') => { if (await onAvis(n, c)) { setNote(n); setOuvert(false); } };
  if (note) return <span className="mt-1 text-[11px] text-slate-400" data-testid={`${testId}-avis-merci`}>{note === 'bon' ? 'Merci pour votre avis' : 'Merci, Klarys en tiendra compte'}</span>;
  return (
    <>
      <button type="button" onClick={() => envoyer('bon')} aria-label="Réponse utile" title="Réponse utile" data-testid={`${testId}-avis-bon`} className="mt-1 text-slate-400 hover:text-emerald-600"><ThumbsUp className="h-3 w-3" /></button>
      <button type="button" onClick={() => setOuvert((o) => !o)} aria-label="Pas utile" title="Pas utile : dites à Klarys quoi corriger" data-testid={`${testId}-avis-mauvais`} className="mt-1 text-slate-400 hover:text-rose-600"><ThumbsDown className="h-3 w-3" /></button>
      {ouvert && (
        <span className="mt-1 flex w-full items-center gap-1.5">
          <input autoFocus value={com} onChange={(e) => setCom(e.target.value)} maxLength={400} placeholder="Qu'aurait-il fallu faire ? (Klarys s'en souviendra)"
            onKeyDown={(e) => { if (e.key === 'Enter') envoyer('mauvais', com); if (e.key === 'Escape') setOuvert(false); }}
            className="flex-1 rounded border border-slate-300 px-2 py-1 text-[11px]" data-testid={`${testId}-avis-commentaire`} />
          <button type="button" onClick={() => envoyer('mauvais', com)} className="rounded bg-[#0a3d62] px-2 py-1 text-[11px] font-semibold text-white" data-testid={`${testId}-avis-envoyer`}>Envoyer</button>
        </span>
      )}
    </>
  );
};

export const KlarysMessage = ({ role, text, testId, headers, dictee, onRenvoyer, busy, onFavori, estFavori, onPartager, onModele, onAvis }) => {
  const user = role === 'user';
  const [edition, setEdition] = useState(false);
  const [copie, setCopie] = useState(false);
  const rendu = useRef(null);
  return (
    <div className={`group flex gap-2 ${user ? 'flex-row-reverse' : ''}`} data-testid={testId}>
      <span className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${user ? 'bg-slate-200 text-slate-700' : 'bg-[#0a3d62] text-amber-300'}`}>
        {user ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </span>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${user ? 'bg-[#0a3d62] text-white rounded-tr-sm' : 'bg-white border border-slate-200 text-slate-800 rounded-tl-sm'}`}>
        {user ? (
          <>
            {edition ? (
              <EditionBulle text={text} testId={testId} busy={busy} onAnnuler={() => setEdition(false)} onRenvoyer={(t) => { setEdition(false); onRenvoyer(t); }} />
            ) : <p className="whitespace-pre-wrap">{text}</p>}
            {!edition && (
              <div className="mt-1 flex items-center justify-end gap-2 text-[10px] text-sky-200">
                {dictee && <span className="inline-flex items-center gap-0.5" data-testid={`${testId}-dictee`}><Mic className="h-3 w-3" /> dictée</span>}
                <span className="flex items-center gap-2 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100" data-testid={`${testId}-outils`}>
                  <button type="button" onClick={() => copier(text, setCopie)} data-testid={`${testId}-copier`} className="inline-flex items-center gap-0.5 hover:text-white">
                    {copie ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copie ? 'Copié' : 'Copier'}
                  </button>
                  {onModele && (
                    <button type="button" onClick={onModele} data-testid={`${testId}-modele`} className="inline-flex items-center gap-0.5 hover:text-white">
                      <FileText className="h-3 w-3" /> Modèle
                    </button>
                  )}
                  {onRenvoyer && (
                    <button type="button" onClick={() => setEdition(true)} data-testid={`${testId}-modifier`} className="inline-flex items-center gap-0.5 hover:text-white">
                      <Pencil className="h-3 w-3" /> Modifier
                    </button>
                  )}
                {onFavori && (
                  <button type="button" onClick={onFavori} disabled={estFavori} data-testid={`${testId}-favori`} className="inline-flex items-center gap-0.5 hover:text-white disabled:text-amber-300">
                    <Star className={`h-3 w-3 ${estFavori ? 'fill-amber-300' : ''}`} /> {estFavori ? 'Favori' : 'Épingler'}
                  </button>
                )}
                {onPartager && (
                  <button type="button" onClick={onPartager} data-testid={`${testId}-partager`} className="inline-flex items-center gap-0.5 hover:text-white">
                    <Share2 className="h-3 w-3" /> Partager
                  </button>
                )}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="klarys-md prose prose-sm max-w-none prose-table:text-xs prose-th:bg-slate-50 prose-th:px-2 prose-td:px-2 prose-p:my-1.5">
            <div ref={rendu}><ReactMarkdown remarkPlugins={[remarkGfm]}>{text || '…'}</ReactMarkdown></div>
            {headers && text && (
              <div className="flex flex-wrap items-center gap-3 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100" data-testid={`${testId}-outils`}>
                <BoutonLire text={text} headers={headers} testId={testId} />
                <button type="button" onClick={() => copierRiche(rendu.current, text, setCopie)} data-testid={`${testId}-copier`} aria-label="Copier la réponse"
                  className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-[#0a3d62]">
                  {copie ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />} {copie ? 'Copié' : 'Copier'}
                </button>
                {onAvis && <Avis testId={testId} onAvis={onAvis} />}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const KlarysEtape = ({ label, testId }) => (
  <div className="ml-9 inline-flex items-center gap-1.5 rounded-full bg-sky-50 border border-sky-100 px-2.5 py-0.5 text-[11px] text-sky-800" data-testid={testId}>
    <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> {label}
  </div>
);
